import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Loader2, Upload, User, Mail, Phone, GraduationCap } from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import toast from 'react-hot-toast';
import R2Image from '../../../components/R2Image';

const FacultyManagement = () => {
  const { userProfile } = useRealtime();
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    qualification: '',
    experience: '',
    subject: '',
    email: '',
    phone: '',
    bio: '',
    photoUrl: ''
  });

  useEffect(() => {
    loadFaculty();
  }, []);

  const loadFaculty = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        setLoading(false);
        return;
      }

      const result = await admissionsService.getInstitutionFaculty(institutionId);
      if (result.success) {
        setFaculty(result.faculty || []);
      } else {
        toast.error('Failed to load faculty');
      }
    } catch (error) {
      toast.error('Error loading faculty');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      const facultyId = editingId || 'temp';
      const result = await admissionsService.uploadFacultyPhoto(file, institutionId, facultyId);

      if (result.success) {
        setFormData(prev => ({
          ...prev,
          photoUrl: result.url
        }));
        toast.success('Photo uploaded successfully');
      } else {
        toast.error('Failed to upload photo: ' + result.error);
      }
    } catch (error) {
      toast.error('Error uploading photo: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (member) => {
    setFormData({
      name: member.name || '',
      designation: member.designation || '',
      qualification: member.qualification || '',
      experience: member.experience || '',
      subject: member.subject || '',
      email: member.email || '',
      phone: member.phone || '',
      bio: member.bio || '',
      photoUrl: member.photoUrl || ''
    });
    setEditingId(member.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      designation: '',
      qualification: '',
      experience: '',
      subject: '',
      email: '',
      phone: '',
      bio: '',
      photoUrl: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter faculty name');
      return;
    }

    try {
      setSaving(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      
      // If new faculty and photo uploaded, upload photo with actual ID after creation
      let photoUrl = formData.photoUrl;
      if (!editingId && formData.photoUrl && formData.photoUrl.includes('temp')) {
        // This will be handled after creation
      }
      
      const result = editingId
        ? await admissionsService.saveFacultyMember({
            ...formData,
            institutionId,
            photoUrl
          }, editingId)
        : await admissionsService.saveFacultyMember({
            ...formData,
            institutionId,
            photoUrl
          });

      if (result.success) {
        // If new faculty and photo needs to be re-uploaded with correct ID
        if (!editingId && formData.photoUrl) {
          // Photo already uploaded, just update the reference
        }
        
        toast.success(editingId ? 'Faculty updated successfully' : 'Faculty added successfully');
        handleCancel();
        loadFaculty();
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving faculty: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this faculty member?')) {
      return;
    }

    try {
      const result = await admissionsService.deleteFacultyMember(id);
      if (result.success) {
        toast.success('Faculty deleted successfully');
        loadFaculty();
      } else {
        toast.error('Failed to delete: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting faculty: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Faculty Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage faculty members and their profiles
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Faculty
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingId ? 'Edit Faculty Member' : 'Add New Faculty Member'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Full Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Designation
                </label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => handleInputChange('designation', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Senior Faculty, HOD"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Qualification
                </label>
                <input
                  type="text"
                  value={formData.qualification}
                  onChange={(e) => handleInputChange('qualification', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., M.Tech, Ph.D."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Experience
                </label>
                <input
                  type="text"
                  value={formData.experience}
                  onChange={(e) => handleInputChange('experience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 10 years"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subject/Department
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., Physics, Mathematics"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="faculty@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="+91 1234567890"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bio
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={4}
                placeholder="Brief biography and achievements..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Photo
              </label>
              {formData.photoUrl ? (
                <div className="flex items-center gap-4">
                  <R2Image
                    fileKey={formData.photoUrl}
                    alt="Faculty"
                    className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                  />
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          <span>Change Photo</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handlePhotoUpload(e.target.files[0]);
                          }
                        }}
                        disabled={uploading}
                      />
                    </label>
                    <button
                      onClick={() => handleInputChange('photoUrl', '')}
                      className="px-4 py-2 text-sm text-red-600 hover:text-red-800"
                    >
                      Remove Photo
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Upload Photo (Max 5MB)</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handlePhotoUpload(e.target.files[0]);
                      }
                    }}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Faculty List */}
      {faculty.length === 0 && !showForm ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400">No faculty members yet. Add your first faculty member!</p>
        </div>
      ) : (
        !showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {faculty.map((member) => (
              <div key={member.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-start gap-4 mb-4">
                  {member.photoUrl ? (
                    <R2Image
                      fileKey={member.photoUrl}
                      alt={member.name}
                      className="w-20 h-20 object-cover rounded-full border-2 border-gray-200 dark:border-gray-700"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <User className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {member.name}
                        </h3>
                        {member.designation && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">{member.designation}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(member)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {member.qualification && (
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">{member.qualification}</span>
                    </div>
                  )}
                  {member.experience && (
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Experience:</span> {member.experience}
                    </p>
                  )}
                  {member.subject && (
                    <p className="text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Subject:</span> {member.subject}
                    </p>
                  )}
                  {member.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">{member.email}</span>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">{member.phone}</span>
                    </div>
                  )}
                  {member.bio && (
                    <p className="text-gray-600 dark:text-gray-400 text-xs mt-2">{member.bio}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default FacultyManagement;
